import React, { useState } from "react";
import { Modal } from "react-bootstrap";

import { ZohoRelatedMeter } from "../../../services/zoho.service";
import { ProposalQuoteSection, SupplierQuote } from "../../../types/proposalDocuments.types";
import {
  MeterFuelType,
  formatMeterName,
  getMeterFuelType,
  getMeterNumber,
} from "../../../utils/proposalDocuments/relatedMeters";
import { validateContractTerm } from "../../../utils/proposalDocuments/validateContractTerm";
import { AVAILABLE_RATES } from "../proposalDocuments.constants";
import { useProposalDocument } from "../proposalDocumentsContext";

import { makeEmptyRate } from "./quoteSection";
import SectionDetailsFields from "./sectionDetailsFields";

interface SectionWizardModalProps {
  show: boolean;
  onClose: () => void;
  onCreate: (section: ProposalQuoteSection) => void;
}

type FuelSelection = "electricity" | "gas" | "dual-fuel";

const STEP_LABELS = ["Details", "Fuel Type", "Meters", "Rates"];

const initialWizardFields = {
  supplier: "",
  product: "",
  contractType: "",
  term: "",
  supplierLogo: null as string | null,
  badge: null as string | null,
  current: false,
};

const SectionWizardModal: React.FC<SectionWizardModalProps> = ({ show, onClose, onCreate }) => {
  const { relatedMeters } = useProposalDocument();

  const [step, setStep] = useState(1);
  const [fields, setFields] = useState(initialWizardFields);
  const [fuelSelection, setFuelSelection] = useState<FuelSelection | null>(null);
  const [selectedMeterIds, setSelectedMeterIds] = useState<Set<string>>(new Set());
  const [selectedElectricRateLabels, setSelectedElectricRateLabels] = useState<Set<string>>(
    new Set(),
  );
  const [selectedGasRateLabels, setSelectedGasRateLabels] = useState<Set<string>>(new Set());
  const [prevShow, setPrevShow] = useState(show);

  // Reset the whole wizard every time it's freshly opened (adjusted during render rather than in an effect)
  if (show !== prevShow) {
    setPrevShow(show);
    if (show) {
      setStep(1);
      setFields(initialWizardFields);
      setFuelSelection(null);
      setSelectedMeterIds(new Set());
      setSelectedElectricRateLabels(new Set());
      setSelectedGasRateLabels(new Set());
    }
  }

  const electricMeters = relatedMeters.filter((m) => getMeterFuelType(m) === "electricity");
  const gasMeters = relatedMeters.filter((m) => getMeterFuelType(m) === "gas");

  // Only offer a fuel choice the customer's meters can actually satisfy -
  // no point offering "Gas" or "Dual Fuel" if there are no gas meters to
  // pick from, and vice versa. Falls back to showing all three if somehow
  // neither meter type is present, so the wizard never dead-ends.
  const availableFuelOptions: [FuelSelection, string][] =
    electricMeters.length === 0 && gasMeters.length === 0
      ? [
          ["electricity", "Electricity"],
          ["gas", "Gas"],
          ["dual-fuel", "Dual Fuel"],
        ]
      : [
          ...(electricMeters.length > 0
            ? ([["electricity", "Electricity"]] as [FuelSelection, string][])
            : []),
          ...(gasMeters.length > 0 ? ([["gas", "Gas"]] as [FuelSelection, string][]) : []),
          ...(electricMeters.length > 0 && gasMeters.length > 0
            ? ([["dual-fuel", "Dual Fuel"]] as [FuelSelection, string][])
            : []),
        ];

  // Fuel choice changing invalidates whatever was picked in the later
  // steps, so reset meter/rate selections back to sensible defaults
  const handleFuelSelect = (selection: FuelSelection) => {
    setFuelSelection(selection);

    const applicableMeters = relatedMeters.filter((m) => {
      const fuel = getMeterFuelType(m);
      if (!fuel) return false;
      return selection === "dual-fuel" ? true : fuel === selection;
    });
    setSelectedMeterIds(new Set(applicableMeters.map((m) => m.id)));
    setSelectedElectricRateLabels(new Set());
    setSelectedGasRateLabels(new Set());
  };

  const handleTermChange = (term: string) => {
    setFields({ ...fields, term });
  };

  const toggleMeter = (id: string) => {
    setSelectedMeterIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRate = (fuel: MeterFuelType, label: string) => {
    const setter =
      fuel === "electricity" ? setSelectedElectricRateLabels : setSelectedGasRateLabels;
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const activeFuelGroups: MeterFuelType[] =
    fuelSelection === "dual-fuel" ? ["electricity", "gas"] : fuelSelection ? [fuelSelection] : [];

  const step1Valid =
    fields.supplier.trim() !== "" && (fields.current || validateContractTerm(fields.term));
  const step2Valid = fuelSelection !== null;
  const step3Valid = activeFuelGroups.every((fuel) => {
    const candidates = fuel === "electricity" ? electricMeters : gasMeters;
    if (candidates.length === 0) return true;
    return candidates.some((m) => selectedMeterIds.has(m.id));
  });
  const step4Valid = activeFuelGroups.every((fuel) => {
    const selected = fuel === "electricity" ? selectedElectricRateLabels : selectedGasRateLabels;
    return selected.size > 0;
  });

  const canProceed =
    (step === 1 && step1Valid) || (step === 2 && step2Valid) || (step === 3 && step3Valid);

  const handleCreate = () => {
    const buildQuoteForMeter = (meter: ZohoRelatedMeter, fuel: MeterFuelType): SupplierQuote => {
      const meterNumber = getMeterNumber(meter, fuel);
      const rateLabels =
        fuel === "electricity" ? selectedElectricRateLabels : selectedGasRateLabels;

      return {
        rates: Array.from(rateLabels).map((label) => makeEmptyRate(label)),
        mpan: fuel === "electricity" ? meterNumber : null,
        mprn: fuel === "gas" ? meterNumber : null,
        zoho_meter_id: meter.id,
        estimated_annual_cost: null,
        annual_consumption: null,
        uplift: 0,
        notes: "",
      };
    };

    const proposed: SupplierQuote[] = relatedMeters.flatMap((m) => {
      const fuel = getMeterFuelType(m);
      return selectedMeterIds.has(m.id) && fuel ? [buildQuoteForMeter(m, fuel)] : [];
    });

    const section: ProposalQuoteSection = {
      proposed,
      current: fields.current,
      annual_consumption: null,
      estimated_annual_cost: null,
      supplier: fields.supplier,
      contract_type: fields.contractType,
      product: fields.product,
      term: fields.current ? null : fields.term,
      supplier_logo: fields.supplierLogo,
      badge: fields.badge,
    };

    onCreate(section);
    onClose();
  };

  const renderMeterGroup = (fuel: MeterFuelType, meters: ZohoRelatedMeter[]) => (
    <div className="section-wizard__meter-group" key={fuel}>
      <h4>{fuel === "electricity" ? "Electricity Meters" : "Gas Meters"}</h4>
      {meters.length === 0 ? (
        <p className="section-wizard__meter-empty">No {fuel} meters found for this customer.</p>
      ) : (
        <ul className="section-wizard__meter-list">
          {meters.map((m) => (
            <li key={m.id} className="section-wizard__meter-item">
              <label>
                <input
                  type="checkbox"
                  checked={selectedMeterIds.has(m.id)}
                  onChange={() => {
                    toggleMeter(m.id);
                  }}
                />
                <span className="section-wizard__meter-name">{formatMeterName(m.meterName)} -</span>
                <span className="section-wizard__meter-number">
                  {fuel === "electricity" ? `MPAN ${m.mpan ?? "—"}` : `MPRN ${m.mprn ?? "—"}`}
                </span>
                <span className="section-wizard__meter-number">{`Zoho ID ${m.id}`}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderRateGroup = (fuel: MeterFuelType) => {
    const rates = AVAILABLE_RATES.filter((r) => r.fuelTypes.includes(fuel));
    const selected = fuel === "electricity" ? selectedElectricRateLabels : selectedGasRateLabels;

    return (
      <div className="section-wizard__rate-group" key={fuel}>
        <h4>{fuel === "electricity" ? "Electricity Rates" : "Gas Rates"}</h4>
        <ul className="section-wizard__rate-list">
          {rates.map((r) => (
            <li key={r.label} className="section-wizard__rate-item">
              <label>
                <input
                  type="checkbox"
                  checked={selected.has(r.label)}
                  onChange={() => {
                    toggleRate(fuel, r.label);
                  }}
                />
                {r.label}
              </label>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>New Quote Section</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="section-wizard__steps">
          {STEP_LABELS.map((label, i) => (
            <div
              key={label}
              className={`section-wizard__step ${step === i + 1 ? "is-active" : ""} ${step > i + 1 ? "is-complete" : ""}`}
            >
              <span className="section-wizard__step-number">{i + 1}</span>
              <span className="section-wizard__step-label">{label}</span>
            </div>
          ))}
        </div>

        {step === 1 && (
          <SectionDetailsFields
            idBase="section-wizard"
            current={fields.current}
            onCurrentChange={(current) => {
              setFields({ ...fields, current });
            }}
            supplier={fields.supplier}
            onSupplierChange={(supplier) => {
              setFields({ ...fields, supplier });
            }}
            product={fields.product}
            onProductChange={(product) => {
              setFields({ ...fields, product });
            }}
            contractType={fields.contractType}
            onContractTypeChange={(contractType) => {
              setFields({ ...fields, contractType });
            }}
            term={fields.term}
            onTermChange={handleTermChange}
            supplierLogo={fields.supplierLogo}
            onSupplierLogoChange={(supplierLogo) => {
              setFields({ ...fields, supplierLogo });
            }}
            badge={fields.badge}
            onBadgeChange={(badge) => {
              setFields({ ...fields, badge });
            }}
          />
        )}

        {step === 2 && (
          <div className="section-wizard__fuel-options">
            {availableFuelOptions.map(([value, label]) => (
              <label
                key={value}
                className={`section-wizard__fuel-option ${fuelSelection === value ? "is-selected" : ""}`}
              >
                <input
                  type="radio"
                  name="section-wizard-fuel-type"
                  checked={fuelSelection === value}
                  onChange={() => {
                    handleFuelSelect(value);
                  }}
                />
                {label}
              </label>
            ))}
          </div>
        )}

        {step === 3 && (
          <div className="section-wizard__meter-selection">
            {(fuelSelection === "electricity" || fuelSelection === "dual-fuel") &&
              renderMeterGroup("electricity", electricMeters)}
            {(fuelSelection === "gas" || fuelSelection === "dual-fuel") &&
              renderMeterGroup("gas", gasMeters)}
          </div>
        )}

        {step === 4 && (
          <div className="section-wizard__rate-selection">
            {activeFuelGroups.map((fuel) => renderRateGroup(fuel))}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {step > 1 && (
          <button
            type="button"
            className="section-wizard__back-button"
            onClick={() => {
              setStep(step - 1);
            }}
          >
            Back
          </button>
        )}
        {step < 4 && (
          <button
            type="button"
            className="section-wizard__next-button"
            disabled={!canProceed}
            onClick={() => {
              setStep(step + 1);
            }}
          >
            Next
          </button>
        )}
        {step === 4 && (
          <button
            type="button"
            className="section-wizard__create-button"
            disabled={!step4Valid}
            onClick={handleCreate}
          >
            Create Section
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SectionWizardModal;
